import type {CodeNavigationInfo, CodeSymbol, DefinitionOrReference} from '@github-ui/code-nav'
import {useCopilotSettings} from '@github-ui/code-view-shared/contexts/CopilotSettingsContext'
import {usePath} from '@github-ui/code-view-shared/contexts/PathContext'
import {SymbolChangedEvent} from '@github-ui/copilot-chat-events'
import type {CopilotChatReference} from '@github-ui/copilot-chat-types'
import CopilotChatButton from '@github-ui/copilot-code-chat/CopilotCodeLinesMenu'
import {useCurrentRepository} from '@github-ui/current-repository'
import {codeNavSearchPath} from '@github-ui/paths'
import {qualifyRef} from '@github-ui/ref-utils'
import type {RefInfo} from '@github-ui/repos-types'
import {GlobalCommands, ScopedCommands} from '@github-ui/ui-commands'
import {useNavigate} from '@github-ui/use-navigate'
import {
  ArrowLeftIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  FoldIcon,
  SearchIcon,
  UnfoldIcon,
  XIcon,
} from '@primer/octicons-react'
import {Button, IconButton, Link as PrimerLink, Spinner, Truncate} from '@primer/react'
import {clsx} from 'clsx'
import {lazy, type PropsWithChildren, Suspense, useEffect, useMemo, useRef, useState} from 'react'

import {useCurrentBlame} from '../../../../hooks/CurrentBlame'
import {useCurrentBlob} from '../../../../hooks/CurrentBlob'
import {useCodeNav} from '../../../../hooks/use-code-nav'
import {useFocusSymbolPane} from '../../../../hooks/use-focus-symbol-pane'
import {scrollLineIntoView} from '../../../../hooks/use-scroll-line-into-view'
import {CodeNavInfoPanelData} from './CodeNavInfoPanelData'
import styles from './CodeNavSymbolDetails.module.css'
import {SymbolIndicator} from './SymbolIndicator'

// Module-level regex constants to avoid recreation on each render
const nonWordBoundaryRegex = /(\W+)/
const nonWordOnlyRegex = /^\W+$/

const MaxCrossReferenceFiles = 5
const ScrollMarks = lazy(() => import('./ScrollMarks'))
const stripLeadingSlash = (path: string) => path.replace(/^\/+/, '')

export function CodeNavSymbolDetails({
  codeNavInfo,
  selectedText,
  lineNumber,
  offset,
  onClose,
  onBackToSymbol,
  onSymbolSelect,
  isLoading,
}: {
  codeNavInfo: CodeNavigationInfo
  selectedText: string
  lineNumber: number
  offset: number
  onClose: () => void
  onBackToSymbol: () => void
  onSymbolSelect: (sym: CodeSymbol) => void
  isLoading: boolean
}) {
  const hasBlame = !!useCurrentBlame()
  const {
    definitions: defsResponse,
    localReferences: localRefsResponse,
    crossReferences: crossRefsResponse,
    error,
  } = useCodeNav(codeNavInfo, selectedText, lineNumber, offset)
  const {copilotAccessAllowed} = useCopilotSettings()
  const [highlightedIndex, setHighlightedIndex] = useState(-1)
  const repo = useCurrentRepository()
  const definitions = useMemo(() => defsResponse?.definitions || [], [defsResponse?.definitions])

  const references = useMemo(() => localRefsResponse?.references || [], [localRefsResponse])

  const crossReferences = useMemo(() => crossRefsResponse?.references || [], [crossRefsResponse?.references])
  const startReferenceIndex = definitions.length
  const allReferencesCount = references.length + crossReferences.length
  const nav = useNavigate()
  const currentPath = usePath()

  const currentSymbol = definitions.length > 0 ? definitions[0] : undefined

  // limit cross references to 5 files
  const crossRefFiles = useMemo(
    () => crossReferences.map(r => r.path).filter((v, i, a) => a.indexOf(v) === i),
    [crossReferences],
  )
  // Initialize from the current cross-ref count so a cache hit shows the right expansion on first
  // paint instead of correcting it after mount.
  const [showAllCrossRefs, setShowAllCrossRefs] = useState(
    () => crossRefFiles.length > 0 && crossRefFiles.length <= MaxCrossReferenceFiles,
  )
  const shownReferences = showAllCrossRefs ? allReferencesCount : references.length

  const backToSymbolsRef = useRef<HTMLButtonElement>(null)

  const {language, languageID} = useCurrentBlob()

  const messageReference: CopilotChatReference = useMemo(
    () => ({
      type: 'symbol',
      kind: 'codeNavSymbol',
      name: selectedText,
      languageID,
      languageName: language,
      codeNavDefinitions: definitions.map(d => ({
        ident: d.ident,
        extent: d.extent,
        kind: d.kind.fullName,
        fullyQualifiedName: d.fullyQualifiedName,
        ref: getRefName(d.refInfo),
        commitOID: d.refInfo.currentOid,
        repoID: d.repo.id,
        repoName: d.repo.name,
        repoOwner: d.repo.ownerLogin,
        path: d.path,
      })),
      codeNavReferences: [
        ...references.map(r => ({
          ident: r.ident,
          path: r.path,
          ref: getRefName(r.refInfo),
          commitOID: r.refInfo.currentOid,
          repoID: r.repo.id,
          repoName: r.repo.name,
          repoOwner: r.repo.ownerLogin,
        })),
        ...crossReferences.map(c => ({
          ident: c.ident,
          path: c.path,
          ref: getRefName(c.refInfo),
          commitOID: c.refInfo.currentOid,
          repoID: c.repo.id,
          repoName: c.repo.name,
          repoOwner: c.repo.ownerLogin,
        })),
      ],
    }),
    [crossReferences, definitions, language, languageID, references, selectedText],
  )

  // Notify Copilot chat of the currently inspected symbol. The clicked-symbol highlight itself is derived
  // in `BlobViewContent` from the selected text, so this effect only synchronizes the external chat context.
  useEffect(() => {
    window.dispatchEvent(new SymbolChangedEvent(messageReference))
  }, [messageReference])

  useFocusSymbolPane(focusSymbolSearch => {
    if (!focusSymbolSearch) {
      backToSymbolsRef.current?.focus()
    }
  })

  // When the selected symbol changes, reset the highlighted index. Derived during render.
  const [prevSelectedText, setPrevSelectedText] = useState(selectedText)
  if (prevSelectedText !== selectedText) {
    setPrevSelectedText(selectedText)
    setHighlightedIndex(-1)
  }

  // Reset the cross-reference expansion default whenever the set of cross-ref files changes.
  const [prevCrossRefFilesLength, setPrevCrossRefFilesLength] = useState(crossRefFiles.length)
  if (prevCrossRefFilesLength !== crossRefFiles.length) {
    setPrevCrossRefFilesLength(crossRefFiles.length)
    setShowAllCrossRefs(crossRefFiles.length > 0 && crossRefFiles.length <= MaxCrossReferenceFiles)
  }

  const prepareReferenceScroll = (reference: DefinitionOrReference) => {
    if (stripLeadingSlash(reference.path) === stripLeadingSlash(currentPath)) {
      // Note: we need this "backdoor" mechanism of scrolling to the selected line, since
      // just setting the URL won't scroll the line into view unless the URL actually changes.
      scrollLineIntoView({line: reference.lineNumber, column: reference.ident.start.column})
    }
  }

  // We should not use navigation on the links.
  // Using href and programmatic navigation breaks nav history on Firefox: https://github.com/github/repos/issues/3462
  const onHighlight = ({index, direction, navigate}: {index?: number; direction?: number; navigate?: boolean}) => {
    // If we are directly setting the index, just do that
    if (index !== undefined) {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const currentRef = index >= startReferenceIndex ? references[index - startReferenceIndex]! : definitions[index]!
      setHighlightedIndex(index)
      prepareReferenceScroll(currentRef)
      if (navigate) {
        nav(currentRef.href(hasBlame))
      }
    }

    if (direction !== undefined) {
      // Account for the fact that the definitions are at the top of the list
      const newIndex = Math.max(startReferenceIndex, highlightedIndex + direction)
      const highlightedSymbol = references[newIndex - startReferenceIndex]
      if (newIndex < references.length + definitions.length && highlightedSymbol) {
        setHighlightedIndex(newIndex)
        prepareReferenceScroll(highlightedSymbol)
        if (navigate) {
          nav(highlightedSymbol.href(hasBlame))
        }
      }
    }
  }

  return (
    <ScopedCommands commands={{'github:cancel': onClose}}>
      <h2 className={styles.detailsHeading} role="group" aria-roledescription="Symbol Navigation Details">
        <Button
          onClick={onBackToSymbol}
          onSelect={onBackToSymbol}
          id="back-to-all-symbols"
          aria-label="Back to all symbols"
          ref={backToSymbolsRef}
          variant="invisible"
          className={styles.backButton}
        >
          <div className={styles.backButtonInner}>
            <ArrowLeftIcon className={styles.backIcon} size={20} />
            <span className={styles.backText}>All symbols</span>
          </div>
        </Button>
        <IconButton
          aria-label="Close symbols"
          tooltipDirection="w"
          icon={XIcon}
          className={styles.closeButton}
          onClick={onClose}
          variant="invisible"
        />
      </h2>
      <div className={styles.definitionHeaderWrapper}>
        <CodeNavSymbolDefinitionHeader
          currentSymbol={currentSymbol}
          selectedText={selectedText}
          codeNavInfo={codeNavInfo}
          onSymbolSelect={onSymbolSelect}
        >
          {definitions.length === 1 ? (
            <CopilotChatButton copilotAccessAllowed={copilotAccessAllowed} messageReference={messageReference} />
          ) : undefined}
        </CodeNavSymbolDefinitionHeader>
      </div>
      {isLoading && (
        <div className={styles.spinnerWrapper}>
          <Spinner size="small" />
        </div>
      )}
      {!error && !isLoading && definitions && definitions.length > 0 ? (
        <>
          <CodeNavSymbolSectionHeader
            headerText={definitions.length > 1 ? 'Definitions' : 'Definition'}
            searchType={defsResponse?.backend === 'precise' ? 'Precise' : 'Search'}
          />
          <div>
            {definitions && (
              <CodeNavInfoPanelData
                key={'definitions'}
                definitions={definitions}
                onClick={(index: number) => {
                  onHighlight({index})
                }}
                highlightedIndex={highlightedIndex}
                initiallyExpanded
                enableExpandCollapse={definitions.length > 1}
                symbol={selectedText}
                setFocusOnFile
              />
            )}
          </div>
        </>
      ) : null}
      {!isLoading && (references.length > 0 || crossReferences.length > 0) && (
        <CodeNavSymbolSectionHeader
          headerText={`${shownReferences} ${shownReferences > 1 ? 'References' : 'Reference'}`}
          searchType="Search"
          className={styles.sectionHeader}
          extraStyle={{justifyContent: 'space-between'}}
        >
          <div className={styles.navButtonWrapper}>
            {/* eslint-disable-next-line primer-react/a11y-remove-disable-tooltip */}
            <IconButton
              unsafeDisableTooltip
              aria-label="Previous reference"
              onClick={() => onHighlight({direction: -1, navigate: true})}
              className={styles.navButtonPrev}
              disabled={highlightedIndex <= definitions.length}
              icon={ChevronUpIcon}
              variant="invisible"
              size="small"
            />
            {/* eslint-disable-next-line primer-react/a11y-remove-disable-tooltip */}
            <IconButton
              unsafeDisableTooltip
              aria-label="Next reference"
              onClick={() => onHighlight({direction: 1, navigate: true})}
              className={styles.navButtonNext}
              disabled={highlightedIndex >= references.length + definitions.length - 1}
              icon={ChevronDownIcon}
              variant="invisible"
              size="small"
            />
            <GlobalCommands
              commands={{
                'code-view:find-next': () => onHighlight({direction: 1, navigate: true}),
                'code-view:find-prev': () => onHighlight({direction: -1, navigate: true}),
              }}
            />
          </div>
        </CodeNavSymbolSectionHeader>
      )}
      {error && <div className={styles.emptyState}>No references found</div>}
      {!isLoading && references.length > 0 && (
        <CodeNavInfoPanelData
          initiallyExpanded
          enableExpandCollapse
          references={references}
          highlightedIndex={highlightedIndex - startReferenceIndex}
          onClick={(index: number) => {
            onHighlight({index: startReferenceIndex + index})
          }}
          key={'referencesInfoBox'}
          symbol={selectedText}
          setFocusOnFile={!(definitions && definitions.length > 0)}
        />
      )}
      {!isLoading && showAllCrossRefs && (
        <CodeNavInfoPanelData
          initiallyExpanded={false}
          enableExpandCollapse
          references={crossReferences}
          key={'crossReferencesInfoBox'}
          symbol={selectedText}
        />
      )}
      {references.length === 0 && definitions.length === 0 && !error && !isLoading && (
        <div className={styles.emptyState}>No definitions or references found</div>
      )}
      <div className={styles.footer}>
        {crossRefFiles.length > MaxCrossReferenceFiles && (
          // If there are more than refs than shown, show toggle to more/less references
          <Button
            leadingVisual={showAllCrossRefs ? FoldIcon : UnfoldIcon}
            className={styles.showMoreButton}
            variant="invisible"
            size="small"
            onClick={() => setShowAllCrossRefs(prev => !prev)}
          >
            {showAllCrossRefs ? 'Show less' : 'Show more'}
          </Button>
        )}

        <Button
          as={PrimerLink}
          leadingVisual={SearchIcon}
          className={styles.searchButton}
          variant="invisible"
          size="small"
          href={codeNavSearchPath({owner: repo.ownerLogin, repo: repo.name, searchTerm: selectedText})}
        >
          Search for this symbol
        </Button>
      </div>
      <Suspense fallback={null}>
        <ScrollMarks definitionsOrReferences={[...definitions, ...references]} />
      </Suspense>
    </ScopedCommands>
  )
}

function CodeNavSymbolSectionHeader({
  headerText,
  searchType,
  className,
  extraStyle,
  children,
}: PropsWithChildren<{
  headerText: string
  searchType: 'Precise' | 'Search'
  className?: string
  extraStyle?: React.CSSProperties
}>) {
  return (
    <div className={clsx(styles.sectionHeader, className)} style={extraStyle}>
      <h3 className={styles.sectionHeading}>
        {headerText}
        <span className={styles.sectionSearchType}>{searchType}</span>
      </h3>
      {children}
    </div>
  )
}

function CodeNavSymbolDefinitionHeader({
  currentSymbol,
  selectedText,
  codeNavInfo,
  onSymbolSelect,
  children,
}: {
  currentSymbol?: CodeSymbol
  selectedText: string
  codeNavInfo: CodeNavigationInfo
  onSymbolSelect: (symbol: CodeSymbol) => void
  children?: React.ReactElement
}) {
  const fullSymbolText = currentSymbol?.fullyQualifiedName ?? selectedText
  const segmentNames = fullSymbolText.split(nonWordBoundaryRegex)

  const symbolSegments = segmentNames.map(segment => {
    const isSeparator = nonWordOnlyRegex.test(segment)
    const foundDefinitions = !isSeparator ? codeNavInfo.getLocalDefinitions(segment, true) : []
    const foundSymbol = foundDefinitions.length === 1 ? foundDefinitions[0] : undefined
    const symbolColor = foundSymbol?.kind.plColor

    return {text: segment, symbol: foundSymbol, symbolColor, isSeparator}
  })

  return (
    <h3
      className={styles.definitionHeader}
      aria-label={`${currentSymbol?.kind.fullName || ''} ${fullSymbolText}`.trimStart()}
    >
      {currentSymbol && (
        <div className={styles.symbolIndicatorRow}>
          <SymbolIndicator symbolKind={currentSymbol.kind} showFullSymbol />
          {children}
        </div>
      )}
      <Truncate title={fullSymbolText} className={styles.symbolTruncate} inline>
        &lrm;
        {symbolSegments.map((segment, index) => {
          // under normal circumstances - use a button and dont do what we are doing below
          // we need a span to allow the truncation to not truncate the entire button element when overflowing
          // the below code makes each non-separator span behave like a button
          return (
            <span
              role="button"
              tabIndex={segment.isSeparator ? -1 : 0}
              className={clsx(styles.symbolSegment, segment.symbol && styles.symbolSegmentClickable)}
              style={{color: segment.symbolColor}}
              // eslint-disable-next-line @eslint-react/no-array-index-key
              key={`${segment.text}-${index}`}
              onClick={() => (segment.symbol ? onSymbolSelect(segment.symbol) : undefined)}
              onKeyDown={(keyboardEvent: React.KeyboardEvent) => {
                if (segment.symbol && ['Enter', 'Space'].includes(keyboardEvent.code)) {
                  onSymbolSelect(segment.symbol)
                }
              }}
            >
              {segment.text}
            </span>
          )
        })}
        &lrm;
      </Truncate>
    </h3>
  )
}

function getRefName(refInfo: RefInfo) {
  return refInfo.name === refInfo.currentOid
    ? refInfo.currentOid
    : refInfo.refType === 'tree'
      ? `refs/heads/${refInfo.name}`
      : qualifyRef(refInfo.name, refInfo.refType ?? 'branch')
}
